import React, { useState, useMemo, useEffect } from "react";
import {
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
  Chip,
  Box,
} from "@mui/material";
import { IFundAccount } from "interfaces/fund-account";
import { useCustom } from "@refinedev/core";
import { IUser } from "interfaces/user";

interface Props {
  fundAccountId: number;
  setSelectedUser: (users: IUser[]) => void;
  selectedAccount: any;
  value?: IUser[];
  showError?: boolean;
  filterEmails?: string[];
  onEmailsChange?: (emails: string[]) => void;
  onFilterEmailsChange?: (emails: string[]) => void;
  // New prop to toggle between "to" and "cc" functionality
  isCC?: boolean;
  // Optional custom labels
  label?: string;
  emptyPlaceholder?: string;
  donorName?: string;
  donorEmail?: string;
}

const UsersEmailSelection = ({
  fundAccountId,
  setSelectedUser,
  value = [],
  showError = false,
  filterEmails = [],
  onEmailsChange,
  onFilterEmailsChange,
  isCC = false, // Default to "to" functionality
  label, // Custom label
  emptyPlaceholder, // Custom empty placeholder
  donorName,
  donorEmail,
}: Props) => {
  const [hasError, setHasError] = useState(false);
  const [hasPreSelected, setHasPreSelected] = useState(false);

  const selectedValues = value;

  const selectedEmails = useMemo(() => {
    return selectedValues.map((user) => user.email).filter((email) => email);
  }, [selectedValues]);

  const filterEmailsKey = filterEmails.join(",");
  useEffect(() => {
    setHasPreSelected(false);
  }, [filterEmailsKey]);

  useEffect(() => {
    if (onEmailsChange) {
      onEmailsChange(selectedEmails);
    }
  }, [filterEmails, selectedEmails, onEmailsChange]);

  const {
    data: fundAccount,
    isLoading,
    isSuccess,
  } = useCustom<IFundAccount>({
    url: `funds/${fundAccountId}?removeSuperAdmin=true`,
    method: "get",
    queryOptions: {
      enabled: !!fundAccountId,
    },
  });

  const fundMembers = useMemo(() => {
    return fundAccount?.data.fundAccountMembers || [];
  }, [fundAccount?.data.fundAccountMembers]);

  const donorUser = useMemo(() => {
    if (!donorEmail) return null;
    const alreadyMember = fundMembers.some(
      (member) =>
        member.user.email?.toLowerCase() === donorEmail.toLowerCase()
    );
    if (alreadyMember) return null;
    return {
      userId: -1,
      firstName: donorName || donorEmail,
      lastName: "",
      email: donorEmail,
    } as IUser;
  }, [donorName, donorEmail, fundMembers]);

  const allUsers = useMemo(() => {
    if (!donorUser) return fundMembers;
    return [{ user: donorUser } as any, ...fundMembers];
  }, [fundMembers, donorUser]);

  const getUserKey = (user: IUser) => user.email || `id-${user.userId}`;

  const usersByKey = useMemo(() => {
    const map = new Map<string, IUser>();
    allUsers.forEach((member) => map.set(getUserKey(member.user), member.user));
    selectedValues.forEach((user) => {
      const key = getUserKey(user);
      if (!map.has(key)) map.set(key, user);
    });
    return map;
  }, [allUsers, selectedValues]);

  useEffect(() => {
    if (
      allUsers.length > 0 &&
      selectedValues.length === 0 &&
      !hasPreSelected
    ) {
      const preSelectedUsers = allUsers
        .filter(
          (member) =>
            member.user.email &&
            (filterEmails.includes(member.user.email) ||
              member.user.email === donorUser?.email)
        )
        .map((member) => member.user);

      if (preSelectedUsers.length > 0) {
        setSelectedUser(preSelectedUsers);
      }
      setHasPreSelected(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, hasPreSelected]);

  const handleUserChange = (event: any) => {
    const selectedKeys = Array.from(
      new Set(event.target.value as string[])
    );
    const selectedUsers = selectedKeys
      .map((key) => usersByKey.get(key))
      .filter((user): user is IUser => !!user);
    setSelectedUser(selectedUsers);

    if (selectedUsers.length > 0) {
      setHasError(false);
    }
  };

  const shouldShowError =
    (showError || hasError) && selectedValues.length === 0;

  const hasNoUsers = !isLoading && (!allUsers || allUsers.length === 0);

  // Dynamic text based on isCC flag
  const getTitleText = () => {
    if (label) return label;
    return isCC
      ? "Please, Choose the users to CC:"
      : "Please, Choose the users to notify:";
  };

  const getEmptyPlaceholder = () => {
    if (emptyPlaceholder) return emptyPlaceholder;
    return "Select Users";
  };

  const getErrorText = () => {
    return isCC
      ? "Please select at least one user to CC"
      : "Please select at least one user to proceed";
  };

  const getEmailSectionTitle = () => {
    return isCC
      ? `CC Emails (${Array.from(new Set([...selectedEmails])).length})`
      : `Emails to notify (${Array.from(new Set([...selectedEmails])).length})`;
  };

  return (
    <Grid item xs={12} sm={12} sx={{ px: 1, pb: 1 }}>
      <Typography variant="bodyCopyRegular">
        {getTitleText()}
      </Typography>

      <FormControl fullWidth sx={{ mt: 1 }} error={shouldShowError}>
        <Select
          multiple
          fullWidth
          name="users"
          size="small"
          value={selectedValues.map(getUserKey)}
          displayEmpty
          onChange={handleUserChange}
          disabled={isLoading || hasNoUsers}
          startAdornment={
            isLoading ? (
              <CircularProgress
                size={16}
                color="inherit"
                sx={{ ml: 1, mr: 1 }}
              />
            ) : null
          }
          renderValue={(selected: string[]) => {
            if (isLoading) {
              return <span style={{ color: "#999" }}>Loading users...</span>;
            }
            if (selected.length === 0) {
              return <em style={{ color: "#999" }}>{getEmptyPlaceholder()}</em>;
            }
            return (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {Array.from(new Set(selected)).map((key) => {
                  const user = usersByKey.get(key);
                  return (
                    <Chip
                      key={key}
                      label={
                        user ? `${user.firstName} ${user.lastName}`.trim() : key
                      }
                      size="small"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            );
          }}
        >
          {isLoading ? (
            <MenuItem disabled>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "16px",
                }}
              >
                <CircularProgress size={20} color="primary" />
                <span style={{ marginLeft: "8px", color: "#666" }}>
                  Loading users...
                </span>
              </div>
            </MenuItem>
          ) : isSuccess && hasNoUsers ? (
            <MenuItem disabled>
              <span style={{ color: "#999", fontStyle: "italic" }}>
                No users available
              </span>
            </MenuItem>
          ) : (
            allUsers?.map((member) => (
              <MenuItem
                key={getUserKey(member.user)}
                value={getUserKey(member.user)}
              >
                {member.user.firstName} {member.user.lastName}
                {member.user.email && (
                  <span
                    style={{
                      color: "#666",
                      marginLeft: "8px",
                      fontSize: "0.9em",
                    }}
                  >
                    ({member.user.email})
                  </span>
                )}
              </MenuItem>
            ))
          )}
        </Select>

        {shouldShowError && (
          <FormHelperText>
            {getErrorText()}
          </FormHelperText>
        )}

        {!isLoading && hasNoUsers && isSuccess && (
          <FormHelperText>No users found for this fund account</FormHelperText>
        )}
      </FormControl>

      {/* Unified Email Display */}
      {(filterEmails.length > 0 || selectedEmails.length > 0) && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="bodyCopyBold" sx={{ pb: 2 }}>
            {getEmailSectionTitle()}
          </Typography>
          {Array.from(new Set([...selectedEmails])).map(
            (email, index, allEmails) => {
              return (
                <Box
                  key={`${email}-${index}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    my: 1,
                    ...(index === allEmails.length - 1 && { mb: 0 }),
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="bodyCopyRegular"
                      color="text.secondary"
                    >
                      • {email}
                    </Typography>
                  </Box>
                </Box>
              );
            }
          )}
        </Box>
      )}
    </Grid>
  );
};

export default UsersEmailSelection;
